import { useState, useRef, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import type { ReconciliationLine, ReconciliationSession } from '../types'
import { LineStatusBadge } from './StatusBadge'
import { fmt, fmtPrice, fmtPriceBasis } from './lineTableFormatters'
import { type ColDef, COLUMNS, STORAGE_VIS, STORAGE_WIDTHS, loadVisible, loadWidths } from './lineTableColumns'

interface Props {
  session: ReconciliationSession
  onLineClick?: (line: ReconciliationLine) => void
  selectedLineId?: number | null
}

export function LineTable({ session, onLineClick, selectedLineId }: Props) {
  const { t } = useTranslation()
  const [visibleCols, setVisibleCols] = useState<Set<string>>(loadVisible)
  const [colWidths, setColWidths]     = useState<Record<string, number>>(loadWidths)
  const [showPanel, setShowPanel]     = useState(false)

  const dragRef = useRef<{ colId: string; startX: number; startWidth: number } | null>(null)

  useEffect(() => {
    localStorage.setItem(STORAGE_VIS, JSON.stringify([...visibleCols]))
  }, [visibleCols])

  useEffect(() => {
    localStorage.setItem(STORAGE_WIDTHS, JSON.stringify(colWidths))
  }, [colWidths])

  const onResizeStart = useCallback((colId: string, e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    dragRef.current = {
      colId,
      startX: e.clientX,
      startWidth: colWidths[colId] ?? COLUMNS.find(c => c.id === colId)?.defaultWidth ?? 80,
    }

    function onMouseMove(ev: MouseEvent) {
      if (!dragRef.current) return
      const newWidth = Math.max(30, dragRef.current.startWidth + ev.clientX - dragRef.current.startX)
      setColWidths(prev => ({ ...prev, [dragRef.current!.colId]: newWidth }))
    }

    function onMouseUp() {
      dragRef.current = null
      document.removeEventListener('mousemove', onMouseMove)
      document.removeEventListener('mouseup', onMouseUp)
    }

    document.addEventListener('mousemove', onMouseMove)
    document.addEventListener('mouseup', onMouseUp)
  }, [colWidths])

  function toggleCol(colId: string) {
    setVisibleCols(prev => {
      const next = new Set(prev)
      if (next.has(colId)) {
        if (next.size <= 1) return prev
        next.delete(colId)
      } else {
        next.add(colId)
      }
      return next
    })
  }

  const activeCols = COLUMNS.filter(c => visibleCols.has(c.id))

  function renderCell(col: ColDef, line: ReconciliationLine, hasPriceMismatch: boolean, hasQtyMismatch: boolean, hasPriceListMismatch: boolean) {
    const priceDelta = hasPriceMismatch && line.ordrsp_price !== null && line.invoice_price !== null
      ? line.invoice_price - line.ordrsp_price
      : null
    const priceDeltaPct = priceDelta !== null && line.ordrsp_price !== 0
      ? (priceDelta / line.ordrsp_price!) * 100
      : null
    const agreedDelta = line.agreed_net_price !== null && line.invoice_price !== null
      ? line.invoice_price - line.agreed_net_price
      : null
    const agreedDeltaPct = agreedDelta !== null && line.agreed_net_price !== null && line.agreed_net_price !== 0
      ? (agreedDelta / line.agreed_net_price) * 100
      : null

    const isNoArticle = line.status === 'NO_ARTICLE_NUMBER'
    const isSingleDoc = line.status === 'SINGLE_DOCUMENT'

    switch (col.id) {
      case 'article_number':
        return (
          <td key={col.id} className="px-2 py-1 font-mono align-top overflow-hidden text-ellipsis" style={{ width: colWidths[col.id] }}>
            {line.article_number.startsWith('[?')
              ? <span className="text-orange-500 dark:text-orange-400 italic">{t('line_table.no_article_number')}</span>
              : (
                <span className="flex items-center gap-1">
                  <span className="text-gray-700 dark:text-gray-300 truncate">{line.article_number}</span>
                  {line.not_in_price_list && (
                    <span className="text-amber-500 dark:text-amber-400 shrink-0" title={t('line_table.article_not_in_price_list')}>⚠</span>
                  )}
                </span>
              )
            }
          </td>
        )

      case 'gtin':
        return (
          <td key={col.id} className="px-2 py-1 font-mono text-gray-500 dark:text-gray-400 align-top overflow-hidden" style={{ width: colWidths[col.id] }}>
            {line.gtin ?? '—'}
          </td>
        )

      case 'description':
        return (
          <td key={col.id} className="px-2 py-1 text-gray-600 dark:text-gray-300 align-top overflow-hidden" title={line.description ?? undefined} style={{ width: colWidths[col.id], whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
            {line.description ?? '—'}
          </td>
        )

      case 'ordrsp_qty':
        return (
          <td key={col.id} className="px-2 py-1 text-right text-gray-700 dark:text-gray-300 align-top tabular-nums" style={{ width: colWidths[col.id] }}>
            {fmt(line.ordrsp_qty)}
          </td>
        )

      case 'desadv_qty':
        return (
          <td key={col.id} className="px-2 py-1 text-right text-gray-700 dark:text-gray-300 align-top tabular-nums" style={{ width: colWidths[col.id] }}>
            {fmt(line.desadv_qty)}
          </td>
        )

      case 'invoice_qty':
        return (
          <td key={col.id} className={`px-2 py-1 text-right font-medium align-top tabular-nums ${hasQtyMismatch ? 'text-red-700 dark:text-red-400' : 'text-gray-700 dark:text-gray-300'}`} style={{ width: colWidths[col.id] }}>
            {fmt(line.invoice_qty)}
            {line.invoice_invoicing_units !== null && !visibleCols.has('invoice_invoicing_units') && (
              <div className="font-normal text-blue-600 dark:text-blue-400 whitespace-nowrap">
                → {fmt(line.invoice_invoicing_units)} {line.price_basis_uom ?? ''}
              </div>
            )}
          </td>
        )

      case 'invoice_invoicing_units':
        return (
          <td key={col.id} className="px-2 py-1 text-right text-blue-700 dark:text-blue-400 align-top tabular-nums" style={{ width: colWidths[col.id] }}>
            {line.invoice_invoicing_units !== null
              ? `${fmt(line.invoice_invoicing_units)}${line.price_basis_uom ? ' ' + line.price_basis_uom : ''}`
              : '—'}
          </td>
        )

      case 'price_basis_qty':
        return (
          <td key={col.id} className="px-2 py-1 text-right text-gray-500 dark:text-gray-400 align-top tabular-nums" style={{ width: colWidths[col.id] }}>
            {line.price_basis_qty !== null
              ? `${fmt(line.price_basis_qty)}${line.price_basis_uom ? ' ' + line.price_basis_uom : ''}`
              : '—'}
          </td>
        )

      case 'unit':
        return (
          <td key={col.id} className="px-2 py-1 text-left text-gray-400 dark:text-gray-500 align-top font-mono" style={{ width: colWidths[col.id] }}>
            {line.unit ?? ''}
          </td>
        )

      case 'ordrsp_price':
        return (
          <td key={col.id} className="px-2 py-1 text-right text-gray-700 dark:text-gray-300 align-top tabular-nums" style={{ width: colWidths[col.id] }}>
            {fmtPrice(line.ordrsp_price)}
          </td>
        )

      case 'invoice_price':
        return (
          <td key={col.id} className={`px-2 py-1 text-right font-medium align-top tabular-nums ${hasPriceMismatch ? 'text-red-700 dark:text-red-400' : 'text-gray-700 dark:text-gray-300'}`} style={{ width: colWidths[col.id] }}>
            {line.price_basis_uom
              ? fmtPriceBasis(line.invoice_price, line.price_basis_qty, line.price_basis_uom)
              : fmtPrice(line.invoice_price)
            }
            {priceDelta !== null && (
              <div className="font-normal italic mt-0.5 text-red-500 dark:text-red-400 whitespace-nowrap">
                {priceDelta > 0 ? '+' : ''}€ {priceDelta.toFixed(2)}
                {priceDeltaPct !== null && (
                  <span className="ml-1 text-red-400 dark:text-red-300">({priceDelta > 0 ? '+' : ''}{priceDeltaPct.toFixed(1)}%)</span>
                )}
              </div>
            )}
          </td>
        )

      case 'agreed_gross_price':
        return (
          <td key={col.id} className="px-2 py-1 text-right text-gray-500 dark:text-gray-400 align-top tabular-nums italic" style={{ width: colWidths[col.id] }}>
            {fmtPrice(line.agreed_gross_price)}
          </td>
        )

      case 'agreed_net_price':
        return (
          <td key={col.id} className={`px-2 py-1 text-right align-top tabular-nums italic ${hasPriceListMismatch ? 'font-medium text-amber-700 dark:text-amber-400' : 'text-gray-500 dark:text-gray-400'}`} style={{ width: colWidths[col.id] }}>
            {fmtPrice(line.agreed_net_price)}
            {agreedDelta !== null && Math.abs(agreedDelta) > 0.001 && (
              <div className="font-normal mt-0.5 text-amber-600 dark:text-amber-400 whitespace-nowrap">
                {agreedDelta > 0 ? '+' : ''}€ {agreedDelta.toFixed(2)}
                {agreedDeltaPct !== null && (
                  <span className="ml-1 text-amber-500 dark:text-amber-300">({agreedDelta > 0 ? '+' : ''}{agreedDeltaPct.toFixed(1)}%)</span>
                )}
              </div>
            )}
          </td>
        )

      case 'ordrsp_net_amount':
        return (
          <td key={col.id} className="px-2 py-1 text-right text-gray-600 dark:text-gray-400 align-top tabular-nums" style={{ width: colWidths[col.id] }}>
            {fmtPrice(line.ordrsp_net_amount)}
          </td>
        )

      case 'invoice_net_amount':
        return (
          <td key={col.id} className="px-2 py-1 text-right text-gray-600 dark:text-gray-400 align-top tabular-nums" style={{ width: colWidths[col.id] }}>
            {fmtPrice(line.invoice_net_amount)}
          </td>
        )

      case 'vat_percentage':
        return (
          <td key={col.id} className="px-2 py-1 text-right text-gray-500 dark:text-gray-400 align-top tabular-nums" style={{ width: colWidths[col.id] }}>
            {line.vat_percentage !== null ? `${line.vat_percentage}%` : '—'}
          </td>
        )

      case 'vat_amount': {
        const vatAmt = line.invoice_net_amount !== null && line.vat_percentage !== null
          ? line.invoice_net_amount * line.vat_percentage / 100
          : null
        return (
          <td key={col.id} className="px-2 py-1 text-right text-gray-500 dark:text-gray-400 align-top tabular-nums italic" style={{ width: colWidths[col.id] }}>
            {fmtPrice(vatAmt)}
          </td>
        )
      }

      case 'status':
        return (
          <td key={col.id} className="px-2 py-1 text-center align-top" style={{ width: colWidths[col.id] }}>
            <LineStatusBadge status={line.status} />
          </td>
        )

      case 'status_details': {
        const detailText = line.status === 'OK'
          ? t('line_table.all_values_correct')
          : line.status_details ?? '—'
        return (
          <td key={col.id} className="px-2 py-1 align-top overflow-hidden" title={detailText} style={{ width: colWidths[col.id], whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
            {line.status === 'OK'
              ? <span className="text-green-600 dark:text-green-400">{t('line_table.all_values_correct')}</span>
              : line.status === 'UOM_CONVERSION'
                ? <span className="text-blue-700 dark:text-blue-400">{line.status_details ?? '—'}</span>
                : isSingleDoc
                  ? <span className="text-gray-400 dark:text-gray-500">—</span>
                  : isNoArticle
                    ? <span className="text-orange-600 dark:text-orange-400">{line.status_details ?? '—'}</span>
                    : <span className="text-red-700 dark:text-red-400">{line.status_details ?? '—'}</span>
            }
          </td>
        )
      }

      default:
        return <td key={col.id} style={{ width: colWidths[col.id] }} />
    }
  }

  return (
    <div className="w-full">

      {/* Toggle-balk: BUITEN het overflow-hidden blok zodat de dropdown niet afgeknipt wordt */}
      <div className="flex items-center justify-between px-3 py-1.5 bg-gray-50/60 dark:bg-gray-900/60 border border-gray-200 dark:border-gray-700 rounded-t-xl">
        <span className="text-[10px] text-gray-400 dark:text-gray-500">
          {t('line_table.columns_visible', { active: activeCols.length, total: COLUMNS.length })}
        </span>
        <div className="relative">
          <button
            onClick={() => setShowPanel(p => !p)}
            className="flex items-center gap-1 px-2 py-1 text-[10px] text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-700 rounded hover:bg-white dark:hover:bg-gray-800 hover:border-gray-300 dark:hover:border-gray-600 transition-colors"
          >
            <svg className="w-3 h-3" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
              <rect x="1" y="3" width="14" height="2" rx="0.5"/>
              <rect x="1" y="7" width="14" height="2" rx="0.5"/>
              <rect x="1" y="11" width="14" height="2" rx="0.5"/>
              <line x1="5" y1="2" x2="5" y2="6"/>
              <line x1="10" y1="6" x2="10" y2="10"/>
              <line x1="7" y1="10" x2="7" y2="14"/>
            </svg>
            {t('line_table.columns_button')}
          </button>
          {showPanel && (
            <div className="absolute right-0 top-full mt-1 z-50 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-2 min-w-[180px]">
              <div className="text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5 px-1">
                {t('line_table.columns_panel_heading')}
              </div>
              {COLUMNS.map(col => (
                <label
                  key={col.id}
                  className="flex items-center gap-2 px-1 py-0.5 rounded cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 text-[11px] text-gray-700 dark:text-gray-300"
                >
                  <input
                    type="checkbox"
                    checked={visibleCols.has(col.id)}
                    onChange={() => toggleCol(col.id)}
                    className="w-3 h-3 accent-indigo-500"
                  />
                  {col.label}
                </label>
              ))}
              <div className="mt-1.5 pt-1.5 border-t border-gray-100 dark:border-gray-800 flex gap-1">
                <button
                  onClick={() => setVisibleCols(new Set(COLUMNS.filter(c => c.defaultVisible).map(c => c.id)))}
                  className="flex-1 text-[10px] text-indigo-600 hover:underline text-center"
                >
                  {t('line_table.columns_reset_default')}
                </button>
                <button
                  onClick={() => setVisibleCols(new Set(COLUMNS.map(c => c.id)))}
                  className="flex-1 text-[10px] text-gray-500 hover:underline text-center"
                >
                  {t('line_table.columns_show_all')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Tabel + totalen: eigen overflow-hidden blok voor afgeronde onderhoeken */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 border-t-0 rounded-b-xl overflow-hidden">
      <div className="overflow-x-auto">
        <table
          className="text-[11px] border-collapse"
          style={{ tableLayout: 'fixed', width: activeCols.reduce((sum, c) => sum + (colWidths[c.id] ?? c.defaultWidth), 0) }}
        >
          <colgroup>
            {activeCols.map(col => (
              <col key={col.id} style={{ width: colWidths[col.id] ?? col.defaultWidth }} />
            ))}
          </colgroup>
          <thead>
            <tr className="bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
              {activeCols.map(col => (
                <th
                  key={col.id}
                  className={`relative px-2 py-1.5 font-medium text-gray-600 dark:text-gray-400 whitespace-nowrap select-none text-${col.align === 'right' ? 'right' : col.align === 'center' ? 'center' : 'left'}`}
                  style={{ width: colWidths[col.id] ?? col.defaultWidth }}
                >
                  {col.label}
                  <div
                    className="absolute top-0 right-0 h-full w-1 cursor-col-resize hover:bg-indigo-300/70 transition-colors"
                    onMouseDown={e => onResizeStart(col.id, e)}
                  />
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
            {session.lines.map(line => {
              const isNoArticle = line.status === 'NO_ARTICLE_NUMBER'
              const isSingleDoc = line.status === 'SINGLE_DOCUMENT'
              const hasQtyMismatch =
                line.desadv_qty !== null &&
                line.invoice_qty !== null &&
                line.desadv_qty !== line.invoice_qty
              const hasPriceMismatch =
                line.ordrsp_price !== null &&
                line.invoice_price !== null &&
                Math.abs(line.ordrsp_price - line.invoice_price) > 0.01
              const hasPriceListMismatch = line.status === 'PRICE_LIST_MISMATCH'

              const isSelected = line.id === selectedLineId
              const rowBg = isSelected
                ? 'bg-indigo-50 dark:bg-indigo-900/20 ring-1 ring-inset ring-indigo-300'
                : isNoArticle ? 'bg-orange-50 dark:bg-orange-950/25'
                : isSingleDoc ? ''
                : line.status !== 'OK' ? 'bg-red-50/40 dark:bg-red-900/10' : ''

              const hasXPath = !!(line.ordrsp_xpath || line.desadv_xpath || line.invoice_xpath)

              return (
                <tr
                  key={line.id}
                  className={`${rowBg} ${onLineClick && hasXPath ? 'cursor-pointer hover:brightness-95' : ''}`}
                  onClick={() => onLineClick && hasXPath ? onLineClick(line) : undefined}
                  title={onLineClick && hasXPath ? t('line_table.click_to_view_xml') : undefined}
                >
                  {activeCols.map(col =>
                    renderCell(col, line, hasPriceMismatch, hasQtyMismatch, hasPriceListMismatch)
                  )}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {session.total_invoice_amount !== null && (
        <div className="border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 px-4 py-4 space-y-3">
          {(session.vat_subtotals?.length ?? 0) > 0 && (
            <div className="flex flex-wrap gap-x-8 gap-y-1">
              {session.vat_subtotals!.map((sub, i) => (
                <div key={i} className="flex items-baseline gap-2 text-sm text-gray-600 dark:text-gray-400">
                  <span className="text-xs text-gray-400 dark:text-gray-500">{t('line_table.vat_percentage', { pct: sub.percentage ?? '?' })}</span>
                  <span>{t('line_table.vat_base', { amount: sub.base_amount !== null ? `€ ${sub.base_amount.toFixed(2)}` : '—' })}</span>
                  <span className="font-medium">{t('line_table.vat_amount_label', { amount: sub.amount !== null ? `€ ${sub.amount.toFixed(2)}` : '—' })}</span>
                </div>
              ))}
            </div>
          )}
          <div className="flex items-end justify-end gap-8 text-sm">
            {session.total_charge_allowance !== null && session.total_charge_allowance !== 0 && (
              <div className="text-right">
                <div className="text-xs text-gray-400 dark:text-gray-500 mb-0.5">{t('line_table.charges_allowances')}</div>
                <div className={`font-medium ${session.total_charge_allowance < 0 ? 'text-green-700 dark:text-green-400' : 'text-orange-700 dark:text-orange-400'}`}>
                  € {session.total_charge_allowance.toFixed(2)}
                </div>
              </div>
            )}
            <div className="text-right">
              <div className="text-xs text-gray-400 dark:text-gray-500 mb-0.5">{t('line_table.total_net')}</div>
              <div className="font-medium text-gray-700 dark:text-gray-300">
                {session.total_net_amount !== null ? `€ ${session.total_net_amount.toFixed(2)}` : '—'}
              </div>
            </div>
            <div className="text-right">
              <div className="text-xs text-gray-400 dark:text-gray-500 mb-0.5">{t('line_table.vat')}</div>
              <div className="font-medium text-gray-700 dark:text-gray-300">
                {session.total_vat_amount !== null ? `€ ${session.total_vat_amount.toFixed(2)}` : '—'}
              </div>
            </div>
            <div className="text-right border-l border-gray-300 dark:border-gray-600 pl-8">
              <div className="text-xs text-gray-400 dark:text-gray-500 mb-0.5">{t('line_table.total_incl_vat')}</div>
              <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                € {session.total_invoice_amount.toFixed(2)}
              </div>
            </div>
          </div>
        </div>
      )}
      </div>{/* einde overflow-hidden tabel+totalen blok */}
    </div>
  )
}
